/**
 * useBulkInvoiceShipmentsByFilters - Hook para prefacturar shipments usando filtros avanzados
 * 
 * Permite prefacturar múltiples shipments basándose en los filtros aplicados mediante
 * POST a /invoices/pre-invoices con el body { advanced_filter: {...} }.
 * Muestra toasts de éxito/error, cierra el modal y ejecuta refetch al completar exitosamente.
 * 
 * @returns {Object} { bulkInvoiceByFilters: función de prefacturación, isInvoicing: estado de carga }
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ShipmentsApiAdapter, PreInvoiceShipmentsPayload } from '../adapters/ShipmentsApiAdapter';
import { apiClientWH } from '@shared/services/ApiClient';
import { createHttpClient } from '@adapters/HttpClient';

export const useBulkInvoiceShipmentsByFilters = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const { toast } = useToast();
  const [isInvoicing, setIsInvoicing] = useState(false);

  const bulkInvoiceByFilters = useCallback(
    async (
      advancedFilters: { filters: any[]; logic: string },
      onSuccess?: () => void,
      additionalFilters?: Omit<PreInvoiceShipmentsPayload, 'shipment_ids' | 'advanced_filters'>
    ) => {
      setIsInvoicing(true);

      try {
        const shipmentsAdapter = new ShipmentsApiAdapter(
          createHttpClient(apiClientWH)
        );
        await shipmentsAdapter.bulkInvoiceShipmentsByFilters(
          advancedFilters,
          additionalFilters
        );

        // 🎯 Si llegamos aquí, la API devolvió 200 OK = éxito
        toast({
          title: t('toast.bulk_invoice_filters_success.title', 'Shipments prefacturados'),
          description: t('toast.bulk_invoice_filters_success.description', 'Los shipments se han prefacturado exitosamente'),
          variant: 'default',
        });

        // 🎯 Ejecutar callback de éxito (refetch de datos)
        onSuccess?.();
      } catch (error: any) {
        // 🎯 Extraer mensaje amigable de la API
        const apiMessage: string | undefined = error?.response?.data?.message;
        const apiErrors: Record<string, string[] | string> | undefined =
          error?.response?.data?.errors;

        let description = apiMessage;

        // Unir los errores detallados si existen
        if (apiErrors && typeof apiErrors === 'object') {
          const details = Object.values(apiErrors)
            .flat()
            .filter(Boolean)
            .join(' • ');
          description = description ? `${description} — ${details}` : details;
        }

        // Fallback de mensaje
        if (!description) {
          description =
            error?.message ||
            t(
              'toast.bulk_invoice_filters_error.unexpected',
              'Error inesperado al prefacturar los shipments. Inténtalo de nuevo.'
            );
        }

        toast({
          title: t('toast.bulk_invoice_filters_error.title', 'Error al prefacturar'),
          description,
          variant: 'destructive',
        });
      } finally {
        setIsInvoicing(false);
      }
    },
    [t, toast]
  );

  return {
    bulkInvoiceByFilters,
    isInvoicing,
  };
};

